/**
 * Éditeur séparé pour la navbar et le footer réutilisables
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { notify } from '../components/NotificationContainer';
import logger from '../utils/logger';

const EditeurNavbarFooter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    navbar_html: '',
    navbar_css: '',
    navbar_js: '',
  });

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Chercher si une page "/" existe
      const response = await api.get('/custom-pages/');
      const pages = response.data.results || response.data;
      const homePage = pages.find(p => p.url_path === '/');

      if (homePage) {
        // La page existe, charger ses détails
        const detailResponse = await api.get(`/custom-pages/${homePage.id}/`);
        setEditingPage(detailResponse.data);

        // Vérifier si la navbar est vide
        const hasNavbar = detailResponse.data.navbar_html && detailResponse.data.navbar_html.trim() !== '';

        if (hasNavbar) {
          // Charger la navbar existante
          setFormData({
            navbar_html: detailResponse.data.navbar_html,
            navbar_css: detailResponse.data.navbar_css || '',
            navbar_js: detailResponse.data.navbar_js || '',
          });
        } else {
          // Si la navbar est vide, charger la navbar par défaut responsive
          const defaultResponse = await api.get('/custom-pages/get_default_homepage/');
          setFormData({
            navbar_html: defaultResponse.data.navbar_html || '',
            navbar_css: defaultResponse.data.navbar_css || '',
            navbar_js: defaultResponse.data.navbar_js || '',
          });
          notify.info('Navbar initialisée avec le modèle par défaut responsive. N\'oubliez pas d\'enregistrer !');
        }
      } else {
        // La page n'existe pas, charger le contenu par défaut
        const defaultResponse = await api.get('/custom-pages/get_default_homepage/');
        setFormData({
          navbar_html: defaultResponse.data.navbar_html || '',
          navbar_css: defaultResponse.data.navbar_css || '',
          navbar_js: defaultResponse.data.navbar_js || '',
        });
      }
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      notify.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const formDataToSend = new FormData();

      if (editingPage) {
        // Mettre à jour la page existante - seulement les champs navbar
        formDataToSend.append('navbar_html', formData.navbar_html);
        formDataToSend.append('navbar_css', formData.navbar_css);
        formDataToSend.append('navbar_js', formData.navbar_js);

        await api.patch(`/custom-pages/${editingPage.id}/`, formDataToSend);
        notify.success('Navbar et footer modifiés avec succès !');
      } else {
        // Créer une nouvelle page d'accueil avec tout le contenu par défaut
        const defaultResponse = await api.get('/custom-pages/get_default_homepage/');

        formDataToSend.append('title', 'Page d\'accueil');
        formDataToSend.append('url_path', '/');
        formDataToSend.append('content_source', 'editor');
        formDataToSend.append('is_active', true);
        formDataToSend.append('html_content', defaultResponse.data.html_content || '<div>Page d\'accueil</div>');
        formDataToSend.append('navbar_html', formData.navbar_html);
        formDataToSend.append('navbar_css', formData.navbar_css);
        formDataToSend.append('navbar_js', formData.navbar_js);

        // Convertir le CSS en fichier si présent
        if (defaultResponse.data.css_content) {
          const cssBlob = new Blob([defaultResponse.data.css_content], { type: 'text/css' });
          formDataToSend.append('css_file', cssBlob, 'styles.css');
        }

        // Convertir le JS en fichier si présent
        if (defaultResponse.data.js_content) {
          const jsBlob = new Blob([defaultResponse.data.js_content], { type: 'text/javascript' });
          formDataToSend.append('js_file', jsBlob, 'script.js');
        }

        const response = await api.post('/custom-pages/', formDataToSend);
        setEditingPage(response.data);
        notify.success('Navbar et footer créés avec succès !');
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReloadDefault = async () => {
    if (!window.confirm('Voulez-vous vraiment recharger le contenu par défaut ? Cela écrasera vos modifications actuelles.')) {
      return;
    }

    try {
      const defaultResponse = await api.get('/custom-pages/get_default_homepage/');
      setFormData({
        navbar_html: defaultResponse.data.navbar_html || '',
        navbar_css: defaultResponse.data.navbar_css || '',
        navbar_js: defaultResponse.data.navbar_js || '',
      });
      notify.success('Contenu par défaut rechargé ! N\'oubliez pas d\'enregistrer.');
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du chargement du contenu par défaut');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Éditeur Navbar & Footer</h1>
              <p className="mt-2 text-sm text-gray-600">
                Modifiez la navbar et le footer qui seront affichés sur les pages personnalisées et la page séjours (non connecté)
              </p>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/admin/pages')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour
            </button>
            <button
              onClick={handleReloadDefault}
              className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recharger par défaut
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Info sur la navbar */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-blue-900 mb-2">
              Navbar réutilisable
            </h2>
            <p className="text-sm text-blue-700">
              Cette navbar sera automatiquement affichée sur :
            </p>
            <ul className="list-disc list-inside text-sm text-blue-700 mt-2 ml-4">
              <li>Les pages personnalisées qui ont l'option "Inclure la navbar" activée</li>
              <li>La page séjours (vue non connecté)</li>
            </ul>
          </div>

          {/* Éditeur HTML Navbar */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              HTML de la navbar
            </label>
            <textarea
              value={formData.navbar_html}
              onChange={(e) => setFormData({ ...formData, navbar_html: e.target.value })}
              rows={15}
              placeholder="<nav>...</nav>"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Code HTML de la navbar (sera affiché en haut des pages)
            </p>
          </div>

          {/* Éditeur CSS Navbar */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CSS de la navbar
            </label>
            <textarea
              value={formData.navbar_css}
              onChange={(e) => setFormData({ ...formData, navbar_css: e.target.value })}
              rows={20}
              placeholder="/* Styles CSS de la navbar */"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Styles CSS spécifiques à la navbar
            </p>
          </div>

          {/* Éditeur JavaScript Navbar */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              JavaScript de la navbar
            </label>
            <textarea
              value={formData.navbar_js}
              onChange={(e) => setFormData({ ...formData, navbar_js: e.target.value })}
              rows={15}
              placeholder="// JavaScript pour la navbar (menu mobile, scroll, etc.)"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Code JavaScript pour les interactions de la navbar (menu mobile, effets de scroll, etc.)
            </p>
          </div>

          {/* Bouton Enregistrer en bas */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002 2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditeurNavbarFooter;
